import { type CSSProperties } from 'react';
import { APP } from './tokens';

// ─── Types ──────────────────────────────────────

export type ExportDestination = 'save' | 'gmail' | 'slack' | 'dropbox' | 'drive' | 'notion' | 'linear';

export interface ExportBarProps {
  variant: 'compact' | 'standard';
  onDestinationClick: (destination: ExportDestination) => void;
  activeDestination?: ExportDestination | null;
  onClose?: () => void;
}

// ─── Destination definitions ────────────────────

interface DestinationDef {
  id: ExportDestination;
  label: string;
  /** Override label for standard variant (e.g. "Save PDF" vs "Save") */
  labelStandard?: string;
  /** Path to logo in /public, or null for inline SVG (Save PDF) */
  logo: string | null;
}

const DESTINATIONS: DestinationDef[] = [
  { id: 'save', label: 'Save', labelStandard: 'Save PDF', logo: null },
  { id: 'gmail', label: 'Gmail', logo: '/IntPointsLogos/Gmail_icon_(2020).svg.png' },
  { id: 'slack', label: 'Slack', logo: '/IntPointsLogos/Slack_icon_2019.svg.png' },
  { id: 'dropbox', label: 'Dropbox', logo: '/IntPointsLogos/dropbox-2-logo-png-transparent.png' },
  { id: 'drive', label: 'Drive', logo: '/IntPointsLogos/Google_Drive_icon_(2020).svg.png' },
  { id: 'notion', label: 'Notion', logo: '/IntPointsLogos/notion-icon.webp' },
  { id: 'linear', label: 'Linear', logo: '/IntPointsLogos/linear-icon.svg' },
];

// ─── Variant sizing ─────────────────────────────

interface VariantTokens {
  containerRadius: number;
  containerPadding: number;
  rowGap: number;
  chipPadding: string;
  chipRadius: number;
  iconSize: number;
  fontSize: number;
}

const VARIANT: Record<'compact' | 'standard', VariantTokens> = {
  compact: {
    containerRadius: 6,
    containerPadding: 10,
    rowGap: 8,
    chipPadding: '4px 7px',
    chipRadius: 5,
    iconSize: 12,
    fontSize: 12,
  },
  standard: {
    containerRadius: 8,
    containerPadding: 12,
    rowGap: 10,
    chipPadding: '6px 10px',
    chipRadius: 6,
    iconSize: 14,
    fontSize: 14,
  },
};

// ─── PDF icon (inline SVG) ──────────────────────

function PdfIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <rect x="3" y="2" width="14" height="16" rx="2" fill="#C13B2A" />
      <text
        x="10"
        y="13"
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize="6"
        fontWeight="700"
        fontFamily={APP.fontFamily}
      >
        PDF
      </text>
    </svg>
  );
}

// ─── Close icon ─────────────────────────────────

function CloseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2.5 2.5L7.5 7.5M7.5 2.5L2.5 7.5" stroke={APP.textExportLabel} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// ─── Component ──────────────────────────────────

export default function ExportBar({ variant, onDestinationClick, activeDestination, onClose }: ExportBarProps) {
  const v = VARIANT[variant];

  const container: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: v.containerRadius,
    padding: v.containerPadding,
    gap: v.rowGap,
    backgroundColor: APP.bgExportBar,
    fontFamily: APP.fontFamily,
  };

  const headerRow: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const headerText: CSSProperties = {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    lineHeight: '12px',
    color: APP.textExportLabel,
  };

  const chipsWrap: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: variant === 'compact' ? 6 : 8,
  };

  return (
    <div style={container}>
      {/* Header row: EXPORT TO + close */}
      <div style={headerRow}>
        <span style={headerText}>Export to</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
            }}
            aria-label="Close export bar"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {/* Destination chips */}
      <div style={chipsWrap}>
        {DESTINATIONS.map((dest) => {
          const isActive = activeDestination === dest.id;
          const chipStyle: CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            gap: variant === 'compact' ? 4 : 5,
            padding: v.chipPadding,
            borderRadius: v.chipRadius,
            border: `1px solid ${isActive ? APP.bgExportChipActive : APP.borderExportChip}`,
            backgroundColor: isActive ? APP.bgExportChipActive : APP.bgExportChip,
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: v.fontSize,
            fontWeight: 500,
            lineHeight: `${v.fontSize + 2}px`,
            color: isActive ? '#FFFFFF' : APP.textPrimary,
          };

          return (
            <button
              key={dest.id}
              type="button"
              className="btn-hover"
              onClick={() => onDestinationClick(dest.id)}
              style={chipStyle}
            >
              {dest.logo ? (
                <img
                  src={dest.logo}
                  alt=""
                  width={v.iconSize}
                  height={v.iconSize}
                  style={{ flexShrink: 0, objectFit: 'contain', borderRadius: 2 }}
                />
              ) : (
                <PdfIcon size={v.iconSize} />
              )}
              {variant === 'standard' && dest.labelStandard ? dest.labelStandard : dest.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
